import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import { errorText } from '../components/custom_creative_session/DevelopmentStage';
import { visibleForSession } from './sessionScope';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * The Hook Selection step's behaviour — ONE implementation, two presentations.
 *
 * WAVE 3, SCREEN 3. Same contract as `usePsychologyMap` and `useAngles`.
 *
 * WHAT THE SWEEP FOUND, before anything was ported. The same family as the two
 * screens before it, which is itself the finding — these were copies of one
 * shape, so they carried copies of one set of defects:
 *
 *   1. `fetchHooks` swallowed every error into `console.error` and left the
 *      list empty, so a failed request and an offer with no hooks were the same
 *      blank screen. Rule 10.
 *   2. A NEW session never fetched at all, and nothing said so — a third route
 *      to that same blank screen.
 *   3. Nothing was scoped to a session.
 *   4. Generation appended with no notice of repeats.
 *   5. Three failure paths used `alert()`.
 *   6. **The response to "Continue" was thrown away.** `select_hooks` returns
 *      `selected_count` from its own `modified_count`, so if an id no longer
 *      matches — stale after a regeneration, say — fewer hooks are selected
 *      than the operator picked. The screen called `onComplete()` regardless
 *      and moved on. **Selecting twelve and getting nine looked exactly like
 *      selecting twelve.** That one is new to this screen; the first five it
 *      shares with Angle Selection.
 *
 * MA-2 landed first, deliberately, so this consumes a finished read contract:
 * the 50-row ceiling is gone (it was hiding 80 hooks on one real offer) and the
 * response carries `total_for_offer`, so the screen can say how many of the
 * offer's hooks it is showing instead of implying it shows them all.
 */
export function useHooks({ offer, sessionId, isNewSession, sessionStartTime,
                           angleId, onComplete, refreshKey = 0 }) {
  const [allHooks, setAllHooks] = useState([]);
  const [selectedHooks, setSelectedHooks] = useState([]);
  const [totalForOffer, setTotalForOffer] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyHook, setBusyHook] = useState(null);   // element_id
  const [feedback, setFeedback] = useState('');

  const [loadState, setLoadState] = useState('idle');
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);

  const [note, setNoteState] = useState(null);
  const setNote = (text) => {
    setNoteState((prev) =>
      !text ? null
      : prev && prev.text === text ? { text, count: prev.count + 1 }
      : { text, count: 1 });
  };

  const fetchHooks = useCallback(async () => {
    setLoadState('loading');
    try {
      // `angle_id` is OPT-IN at the endpoint (MA-2): omitted, it returns the
      // whole offer exactly as before. Passing it is what a multi-angle track
      // will do; nothing passes it yet, and the screen behaves identically.
      // `session_id` makes the response say which hooks THIS session picked.
      // Without it the screen restored its selection from the offer-wide flag
      // and showed another session's picks as the operator's own.
      const params = {};
      if (angleId) params.angle_id = angleId;
      if (sessionId) params.session_id = sessionId;
      const res = await axios.get(`${API}/offers/${offer.offer_id}/hooks`,
                                  Object.keys(params).length ? { params } : undefined);
      const rows = res.data.hooks || [];
      setAllHooks(rows);
      setTotalForOffer(
        typeof res.data.total_for_offer === 'number' ? res.data.total_for_offer : rows.length);
      // THIS session's picks when the backend says so; the offer-wide flag only
      // when it does not — a hook selected before session scoping existed.
      const already = rows
        .filter((h) => ('selected_in_session' in h ? h.selected_in_session : h.selected))
        .map((h) => h.element_id);
      if (already.length) setSelectedHooks(already);
      setLoadState('ok');
      setLoadError(null);
    } catch (error) {
      setLoadState('failed');
      setLoadError(errorText(error));
    }
  }, [offer.offer_id, angleId, sessionId]);

  useEffect(() => {
    fetchHooks();
  }, [fetchHooks, refreshKey]);

  const generateHooks = async () => {
    setGenerating(true);
    setActionError(null);
    try {
      const res = await axios.post(`${API}/offers/${offer.offer_id}/hooks/generate`, {
        feedback: feedback || undefined,
        session_id: sessionId || undefined,
        angle_id: angleId || undefined,
      });
      const returned = res.data.hooks || [];
      // The backend salvages what parses from a malformed answer and says what
      // it lost. Surfaced verbatim — a partial batch presented as a whole one
      // is the silent success this screen family keeps being fixed for.
      const partial = res.data.partial_note || null;

      // Computed synchronously from the CURRENT list. Reading it back inside
      // the state updater gave 0 every time on screen 1.
      const onScreen = new Set(
        allHooks.map((h) => (h.content || '').trim().toLowerCase()));
      const repeats = returned.filter(
        (h) => onScreen.has((h.content || '').trim().toLowerCase())).length;

      if (returned.length) setAllHooks((prev) => [...prev, ...returned]);
      setFeedback('');

      // HOW MANY ACTUALLY APPEARED, not how many were saved.
      //
      // THE DEFECT THIS FIXES, seen live 2026-09-09: the note said "Added 5
      // hooks" while the grid showed none. It was counting what came BACK
      // while the grid renders what is VISIBLE after scoping, and the two had
      // diverged — the hooks had saved without a session stamp, so the new
      // session hid them. The stamp is fixed at the source, but a message that
      // claims work the operator cannot see is rule 10 whatever the cause, so
      // the count is now taken from the same list the grid draws.
      const opts = { isNewSession, sessionStartTime };
      const before = visibleForSession(allHooks, sessionId, opts).rows.length;
      const after = visibleForSession([...allHooks, ...returned], sessionId, opts).rows.length;
      const appeared = after - before;

      const withPartial = (t) => (partial ? `${partial} ${t}` : t);
      setNote(withPartial(
        returned.length === 0
          ? 'The model returned no hooks. Nothing was added.'
          : appeared === 0
            ? `Saved ${returned.length} hook${returned.length > 1 ? 's' : ''}, but `
              + `none of them are shown here — this view is scoped to the current `
              + `session, and they are not stamped with it. They are on the offer.`
            : appeared < returned.length
              ? `Saved ${returned.length}; ${appeared} shown here. The rest are on `
                + `the offer but outside this session's view.`
              : repeats === 0
                ? `Added ${returned.length} hook${returned.length > 1 ? 's' : ''}.`
                : repeats === returned.length
                  ? `Added ${returned.length}, but every one repeats a hook already on screen.`
                  : `Added ${returned.length}; ${repeats} repeat${repeats > 1 ? '' : 's'} a hook already on screen.`));
    } catch (error) {
      setActionError(errorText(error));
    } finally {
      setGenerating(false);
    }
  };

  const toggleHook = (id) => {
    setSelectedHooks((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  /**
   * Commit the selection, and CHECK WHAT CAME BACK.
   *
   * `select_hooks` reports `selected_count` from its own `modified_count`. An
   * id that no longer matches the offer is silently not selected — and the old
   * screen called `onComplete()` without ever reading the number, so selecting
   * twelve and getting nine was indistinguishable from selecting twelve. The
   * step does not advance on a mismatch; it says what happened and stays put,
   * because moving on to script generation with fewer hooks than the operator
   * chose is the kind of quiet wrong answer rule 10 exists to stop.
   */
  const confirmHooks = async () => {
    if (selectedHooks.length === 0) {
      setActionError('Select at least one hook to continue.');
      return;
    }
    setSaving(true);
    setActionError(null);
    try {
      const res = await axios.post(`${API}/offers/${offer.offer_id}/hooks/select`,
                                   { hook_element_ids: selectedHooks,
                                     session_id: sessionId || undefined });
      const count = res.data?.selected_count;
      if (typeof count === 'number' && count !== selectedHooks.length) {
        setActionError(
          `You selected ${selectedHooks.length} hook${selectedHooks.length > 1 ? 's' : ''}, `
          + `but only ${count} could be recorded. Nothing has moved on — reload `
          + `this step and choose again, in case some of these hooks were `
          + `replaced by a regeneration.`);
        return;
      }
      if (onComplete) onComplete();
    } catch (error) {
      setActionError(errorText(error));
    } finally {
      setSaving(false);
    }
  };

  /** Replace one hook in place, whatever changed it. */
  const merge = (updated) => {
    if (!updated?.element_id) return;
    setAllHooks((prev) => prev.map(
      (h) => (h.element_id === updated.element_id ? { ...h, ...updated } : h)));
  };

  /**
   * Rewrite ONE hook from a note — the model does it, the angle is kept.
   *
   * THE FOUNDER, 2026-09-10: "on Hooks I need a refine button, because I can't
   * make changes to the hooks here and I would like to." The Angle step has had
   * refine and edit since item 4; this step had neither, so changing one hook
   * meant regenerating all five and losing the four that were fine.
   */
  const refineHook = async (elementId, text) => {
    const note = (text || '').trim();
    if (note.length < 3) return false;
    setBusyHook(elementId);
    setActionError(null);
    try {
      const res = await axios.post(
        `${API}/offers/${offer.offer_id}/hooks/${elementId}/refine`,
        { feedback: note });
      merge(res.data.hook);
      return true;
    } catch (error) {
      setActionError(errorText(error));
      return false;
    } finally {
      setBusyHook(null);
    }
  };

  /** Apply the operator's own words. No model. */
  const editHook = async (elementId, changes) => {
    setBusyHook(elementId);
    setActionError(null);
    try {
      const res = await axios.patch(
        `${API}/offers/${offer.offer_id}/hooks/${elementId}`, changes);
      merge(res.data.hook);
      return true;
    } catch (error) {
      // The server refuses a hook a script was already written from, and names
      // the script. That reason has to reach the screen intact.
      setActionError(errorText(error));
      return false;
    } finally {
      setBusyHook(null);
    }
  };

  const view = visibleForSession(allHooks, sessionId, { isNewSession, sessionStartTime });

  return {
    hooks: view.rows,
    hiddenOtherSession: view.hiddenOtherSession,
    hiddenUnstamped: view.hiddenUnstamped,
    predateNote: view.predateNote,
    allCount: allHooks.length,
    totalForOffer,
    selectedHooks, toggleHook,
    loadState, loadError, retryLoad: fetchHooks,
    generating, generateHooks, feedback, setFeedback,
    saving, confirmHooks,
    busyHook, refineHook, editHook,
    actionError, setActionError,
    note,
  };
}
